import { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
    Cube, User as UserIcon, ShieldCheck, TrashSimple, Broom, Timer, Lock, House,
    CaretRight, ArrowClockwise, Download, Warning, SignIn, SignOut, Calendar, PencilSimple,
    Archive,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const KIND_META = {
    admin_login:                 { label: "Admin signed in",        Icon: SignIn, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    admin_logout:                { label: "Admin signed out",       Icon: SignOut, color: "text-slate-700 bg-slate-50 border-slate-200" },
    voter_impersonation_started: { label: "Impersonation started",  Icon: UserIcon, color: "text-amber-700 bg-amber-50 border-amber-200" },
    voter_impersonation_stopped: { label: "Impersonation stopped",  Icon: UserIcon, color: "text-slate-700 bg-slate-50 border-slate-200" },
    election_created:            { label: "Election created",       Icon: PencilSimple, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    election_deleted:            { label: "Election deleted",       Icon: TrashSimple, color: "text-rose-700 bg-rose-50 border-rose-200" },
    election_status_changed:     { label: "Status changed",         Icon: CaretRight, color: "text-civic-700 bg-civic-50 border-civic-100" },
    election_schedule_changed:   { label: "Schedule changed",       Icon: Calendar, color: "text-amber-700 bg-amber-50 border-amber-200" },
    election_updated:            { label: "Election updated",       Icon: PencilSimple, color: "text-civic-700 bg-civic-50 border-civic-100" },
    election_auto_closed:        { label: "Auto-closed",            Icon: Timer, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    election_archived:           { label: "Election archived",      Icon: Archive, color: "text-slate-700 bg-slate-100 border-slate-300" },
    election_unarchived:         { label: "Election unarchived",    Icon: Archive, color: "text-civic-700 bg-civic-50 border-civic-100" },
    email_blast_sent:            { label: "Email blast sent",       Icon: SignOut, color: "text-purple-700 bg-purple-50 border-purple-200" },
    email_blast_attempt_failed:  { label: "Email blast failed",     Icon: Warning, color: "text-rose-700 bg-rose-50 border-rose-200" },
    voter_created:               { label: "Voter created",          Icon: UserIcon, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    voter_updated:               { label: "Voter updated",          Icon: PencilSimple, color: "text-civic-700 bg-civic-50 border-civic-100" },
    voter_deleted:               { label: "Voter deleted",          Icon: TrashSimple, color: "text-rose-700 bg-rose-50 border-rose-200" },
    voter_pin_changed:           { label: "Voter PIN changed",      Icon: Lock, color: "text-amber-700 bg-amber-50 border-amber-200" },
    voter_pin_reset_by_admin:    { label: "PIN reset by admin",     Icon: Lock, color: "text-amber-700 bg-amber-50 border-amber-200" },
    voter_address_changed:       { label: "Voter address changed",  Icon: House, color: "text-amber-700 bg-amber-50 border-amber-200" },
    voters_imported:             { label: "Voters imported",        Icon: UserIcon, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    votes_wiped_all:             { label: "Votes wiped (ALL)",      Icon: Broom, color: "text-rose-700 bg-rose-50 border-rose-200" },
    votes_wiped_for_elections:   { label: "Votes wiped (selected)", Icon: Broom, color: "text-amber-700 bg-amber-50 border-amber-200" },
    voters_wiped_all:            { label: "Voters wiped (ALL)",     Icon: Broom, color: "text-rose-700 bg-rose-50 border-rose-200" },
    email_log_wiped:             { label: "Email history wiped",    Icon: Broom, color: "text-slate-700 bg-slate-50 border-slate-200" },
    settings_updated:            { label: "Settings updated",       Icon: PencilSimple, color: "text-civic-700 bg-civic-50 border-civic-100" },
    backup_downloaded:           { label: "Backup downloaded",      Icon: Download, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    backup_emailed:              { label: "Backup emailed",         Icon: Download, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    backup_restored:             { label: "Backup restored",        Icon: ArrowClockwise, color: "text-amber-700 bg-amber-50 border-amber-200" },
    super_admin_created:         { label: "Super admin created",    Icon: ShieldCheck, color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    super_admin_updated:         { label: "Super admin updated",    Icon: ShieldCheck, color: "text-civic-700 bg-civic-50 border-civic-100" },
    super_admin_deleted:         { label: "Super admin deleted",    Icon: ShieldCheck, color: "text-rose-700 bg-rose-50 border-rose-200" },
    admin_log_purged:            { label: "Audit log purged",       Icon: Broom, color: "text-slate-700 bg-slate-100 border-slate-300" },
};

const KIND_FILTERS = [
    { value: "", label: "All events" },
    { value: "admin_login", label: "Admin sign-ins" },
    { value: "admin_logout", label: "Admin sign-outs" },
    { value: "election_schedule_changed", label: "Schedule changes" },
    { value: "election_status_changed", label: "Status changes" },
    { value: "election_updated", label: "Election updates" },
    { value: "election_auto_closed", label: "Auto-closed" },
    { value: "election_deleted", label: "Election deletions" },
    { value: "voter_deleted", label: "Voter deletions" },
    { value: "voter_pin_changed", label: "PIN changes" },
    { value: "voter_pin_reset_by_admin", label: "Admin PIN resets" },
    { value: "voter_address_changed", label: "Address changes" },
    { value: "email_blast_sent", label: "Email blasts" },
    { value: "admin_log_purged", label: "Log purges" },
];

function relativeTime(iso) {
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return `${Math.floor(diff)}s ago`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        if (diff < 86400 * 30) return `${Math.floor(diff / 86400)}d ago`;
        return d.toLocaleDateString();
    } catch { return iso; }
}

function formatDetail(kind, details) {
    if (!details || Object.keys(details).length === 0) return null;
    if (kind === "election_status_changed") return `${details.from || "—"} → ${details.to}`;
    if (kind === "election_auto_closed") return `${details.votes_cast}/${details.eligible_voters} voters`;
    if (kind === "election_deleted") return `${details.votes_removed || 0} vote${details.votes_removed === 1 ? "" : "s"} removed · was ${details.status_at_delete || "—"}`;
    if (kind === "voter_deleted") return `${details.blocks_removed || 0} block${details.blocks_removed === 1 ? "" : "s"} removed · precinct ${details.precinct || "—"}`;
    if (kind === "voter_address_changed") return `${(details.from || "—").slice(0, 40)} → ${(details.to || "").slice(0, 40)}`;
    if (kind === "voter_pin_changed") return "self-service PIN reset";
    if (kind === "voter_pin_reset_by_admin") return `admin-reset PIN · voter ${details.voter_id || "—"} · precinct ${details.precinct || "—"}`;
    if (kind === "email_blast_sent") {
        const bits = [];
        if (details.kind) bits.push(details.kind);
        const s = details.sent ?? 0;
        const f = details.failed ?? 0;
        const eligible = details.eligible;
        bits.push(`${s} sent`);
        if (f > 0) bits.push(`${f} failed`);
        if (eligible != null) bits.push(`of ${eligible} eligible`);
        return bits.join(" · ");
    }
    if (kind === "email_blast_attempt_failed") {
        const bits = [];
        if (details.kind) bits.push(details.kind);
        if (details.reason) bits.push(`reason: ${details.reason}`);
        return bits.join(" · ") || null;
    }
    if (kind === "admin_log_purged") return `${details.removed || 0} entries removed · older than ${details.older_than_days}d`;
    if (kind === "admin_login") {
        const bits = [];
        if (details.exempt_2fa) bits.push("2FA-exempt (system admin)");
        else if (details.method === "password+2fa") bits.push("password + 2FA");
        if (details.remember_me) bits.push("remember me");
        return bits.join(" · ") || null;
    }
    if (kind === "election_schedule_changed" || kind === "election_updated") {
        const c = details.changes || {};
        const keys = Object.keys(c);
        if (keys.length === 0) return null;
        const short = (v) => {
            if (v == null) return "—";
            if (typeof v === "string" && v.length > 22) return v.slice(0, 19) + "…";
            if (Array.isArray(v)) return `[${v.length}]`;
            return String(v);
        };
        return keys.slice(0, 3).map((k) => {
            const change = c[k];
            if (change && "count_from" in change) return `${k}: ${change.count_from} → ${change.count_to}`;
            return `${k}: ${short(change.from)} → ${short(change.to)}`;
        }).join(" · ");
    }
    return null;
}

export default function AuditTrailPanel() {
    const [rows, setRows] = useState([]);
    const [kind, setKind] = useState("");
    const [loading, setLoading] = useState(false);
    const [purgeDays, setPurgeDays] = useState(730);
    const [purging, setPurging] = useState(false);

    const load = async (k = kind) => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/audit`, { params: { limit: 500, ...(k ? { kind: k } : {}) } });
            setRows(data.entries || []);
        } catch (e) { toast.error(e.response?.data?.detail || "Could not load audit log"); }
        setLoading(false);
    };

    useEffect(() => { load(""); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const runPurge = async () => {
        const days = Number(purgeDays) || 0;
        if (days < 1) { toast.error("Enter a number of days ≥1"); return; }
        if (!window.confirm(`Purge every audit entry older than ${days} days? This can't be undone.`)) return;
        setPurging(true);
        try {
            const { data } = await api.delete(`/admin/audit/purge`, { params: { older_than_days: days } });
            toast.success(`Purge complete · ${data.removed} entries removed`);
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Purge failed"); }
        setPurging(false);
    };

    const downloadCsv = () => {
        if (!rows.length) return;
        const cols = ["Date", "Kind", "Actor", "Actor role", "Target", "Detail"];
        const esc = (v) => {
            const s = (v ?? "").toString();
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        };
        const csv = [cols.join(","), ...rows.map((r) => [
            r.created_at,
            r.kind,
            r.actor_email || "(system)",
            r.actor_role || "",
            r.target_label || r.target_id || "",
            JSON.stringify(r.details || {}),
        ].map(esc).join(","))].join("\n");
        const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `admin_audit_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    return (
        <div className="space-y-4" data-testid="audit-trail-panel">
            {/* Filter chips + actions */}
            <Card className="p-3 bg-white border-slate-200 flex flex-wrap items-center gap-2">
                <div className="flex flex-wrap gap-1.5 flex-1">
                    {KIND_FILTERS.map((f) => (
                        <button
                            key={f.value}
                            type="button"
                            onClick={() => { setKind(f.value); load(f.value); }}
                            className={`text-[11px] hash-mono px-2.5 py-1 rounded-full border transition-colors ${
                                kind === f.value
                                    ? "bg-civic-600 text-white border-civic-600"
                                    : "bg-white text-slate-700 border-slate-200 hover:border-civic-300 hover:bg-civic-50"
                            }`}
                            data-testid={`audit-filter-${f.value || "all"}`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <Button variant="outline" size="sm" onClick={() => load()} disabled={loading} data-testid="audit-refresh" className="h-8">
                    <ArrowClockwise size={12} className="mr-1" /> {loading ? "Loading" : "Refresh"}
                </Button>
                <Button variant="outline" size="sm" onClick={downloadCsv} disabled={!rows.length} data-testid="audit-csv" className="h-8 border-civic-200 hover:bg-civic-50 text-civic-700">
                    <Download size={12} className="mr-1" weight="bold" /> CSV
                </Button>
            </Card>

            {/* Timeline */}
            <Card className="p-0 bg-white border-slate-200 overflow-hidden">
                {rows.length === 0 ? (
                    <div className="p-8 text-center text-sm text-slate-500 italic">
                        {loading ? "Loading audit trail…" : "No audit events yet in this window."}
                    </div>
                ) : (
                    <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto" data-testid="audit-trail-list">
                        {rows.map((r) => {
                            const meta = KIND_META[r.kind] || { label: r.kind, Icon: Cube, color: "text-slate-700 bg-slate-100 border-slate-200" };
                            const Icon = meta.Icon;
                            const detail = formatDetail(r.kind, r.details);
                            return (
                                <div key={r.id} className="px-3 py-2.5 flex items-start gap-3 text-sm hover:bg-slate-50" data-testid={`audit-row-${r.id}`}>
                                    <span className={`h-7 w-7 grid place-items-center rounded-md border ${meta.color} shrink-0`}>
                                        <Icon size={13} weight="fill" />
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                            <span className="font-semibold text-slate-900">{meta.label}</span>
                                            {r.target_label && <span className="text-slate-500 truncate">· {r.target_label}</span>}
                                            {detail && <span className="hash-mono text-[11px] text-slate-500">· {detail}</span>}
                                        </div>
                                        <div className="hash-mono text-[10px] text-slate-500 mt-0.5">
                                            {r.actor_email
                                                ? <><UserIcon size={9} className="inline mr-0.5" /> {r.actor_email} ({r.actor_role})</>
                                                : <><ShieldCheck size={9} className="inline mr-0.5" /> system</>}
                                            <span className="mx-1.5">·</span>
                                            <span title={r.created_at}>{relativeTime(r.created_at)}</span>
                                        </div>
                                    </div>
                                    <Badge variant="outline" className="text-[9px] hash-mono text-slate-500 border-slate-200 shrink-0 hidden sm:inline-flex">
                                        {r.kind}
                                    </Badge>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>

            {/* Retention control */}
            <Card className="p-4 bg-amber-50/60 border-amber-200" data-testid="audit-retention-card">
                <div className="flex items-start gap-3">
                    <Warning size={20} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <div className="font-heading font-bold text-sm text-amber-900">Retention · purge old audit entries</div>
                        <p className="text-[12px] text-amber-800 leading-snug mt-1">
                            Delete audit-log entries older than the number of days below. Default is 730 (≈2 years). This action can&apos;t be undone; the purge itself is logged.
                        </p>
                        <div className="flex flex-wrap items-center gap-2 mt-3">
                            <Input
                                type="number"
                                min={1}
                                value={purgeDays}
                                onChange={(e) => setPurgeDays(e.target.value)}
                                className="w-28 h-8 hash-mono"
                                data-testid="audit-purge-days"
                            />
                            <span className="text-xs text-amber-800">days old</span>
                            <Button
                                onClick={runPurge}
                                disabled={purging}
                                className="bg-amber-600 hover:bg-amber-700 text-white h-8 text-sm"
                                data-testid="audit-purge-btn"
                            >
                                {purging ? "Purging…" : "Purge entries"}
                            </Button>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
}
